import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import Button from 'components/Button';

import Styled from './styles';

export interface YearProps extends React.ComponentPropsWithRef<'span'> {
  /** The formatted year. */
  value?: React.ReactNode,
  /** Set to `true`, to enable the Button. */
  disabled?: boolean,
  /** `<Button/>` onClick callback, can be used to toggle the `<YearPicker/>` */
  onClick?: React.MouseEventHandler
}
export interface CalendarHeaderProps extends Omit<React.ComponentPropsWithRef<'span'>, 'title'> {
  /**
   * Year element configuration.
   * If `disabled` is set to `false`, year will be rendered as a CTA `<Button/>`.
   * If it is enabled, use this prop to configure the year `<Button/>`. See `<Button>` documentation.
   * */
  year?: YearProps;
  /** Set to `true`, to render header in error state. */
  error?: boolean;
  /** Formatted date, rendered on the header. */
  header?: React.ReactNode;
  /** Header element attributes. */
  headerProps?: React.ComponentPropsWithRef<'span'>;
  /** The title of the header. E.g. "From" or "To" */
  title?: React.ReactNode;
  /** Title element attributes. */
  titleProps?: React.ComponentPropsWithRef<'span'>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/**
 * All other props will be provided to the root DOM element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 */
const CalendarHeader = React.forwardRef<HTMLElement, CalendarHeaderProps>((props: CalendarHeaderProps, ref) => getContext(({ RTL, theme }) => {
  const {
    header,
    headerProps,
    year,
    theme: themeProps,
    title,
    titleProps,
    ...otherProps
  } = props;
  const {
    value,
    disabled: isYearDisabled,
    ref: yearRef,
    ...otherYearProps
  } = year;
  const resolvedTheme = _merge({}, theme.CALENDAR.HEADER, themeProps);

  const renderTitle = () =>
    (title !== undefined && title !== '')
    && <Styled.Title $rtl={RTL} $theme={resolvedTheme} {...titleProps}>{title}</Styled.Title>;

  const renderHeader = () =>
    (header !== undefined && header !== '')
    && <Styled.Header $rtl={RTL} $theme={resolvedTheme} {...headerProps}>{header}</Styled.Header>;

  const renderYear = () => value !== undefined ?
    isYearDisabled
      ? <Styled.Year $theme={resolvedTheme.YEAR} ref={yearRef} {...otherYearProps}><span>{value}</span></Styled.Year>
      : (
        <Styled.YearButtonWrapper>
          <Button variant="call-to-action" ref={yearRef} {...otherYearProps}>{value}</Button>
        </Styled.YearButtonWrapper>)
    : null;

  return (
    <Styled.CalendarHeader
      $rtl={RTL}
      ref={ref}
      $theme={resolvedTheme}
      {...otherProps}
    >
      {renderTitle()}
      {renderYear()}
      {renderHeader()}
    </Styled.CalendarHeader>
  );
}));

CalendarHeader.defaultProps = {
  year: {
    disabled: true,
    value: undefined,
    onClick: () => {}
  },
  error: false,
  header: undefined,
  headerProps: {},
  title: undefined,
  titleProps: {},
  theme: {}
};

CalendarHeader.displayName = 'CalendarHeader';

export default CalendarHeader;
